import ConversationReadingPane from './ConversationReadingPane';
import EmptyStateReadingPane from './EmptyStateReadingPane';
import ItemReadingPane from './ItemReadingPane';
import NullReadingPane from './NullReadingPane';
import PrintItemReadingPane from './PrintItemReadingPane';
import PrintPanel from './PrintPanel';
import { recoverableItemsFolderDescription } from './ReadingPane.locstring.json';
import { isGetMobileFeatureEnabled } from '../utils/isGetMobileFeatureEnabled';
import { observer } from 'mobx-react';
import { SelectionType } from 'owa-addins-core';
import { lazyInitializeGetItemManagerForRP } from 'owa-get-item-manager-initialization';
import loc from 'owa-localize';
import { updateAddinOnItemNavigation } from 'owa-mail-addins';
import findInlineComposeViewState from 'owa-mail-compose-actions/lib/utils/findInlineComposeViewState';
import { isReadingPanePositionOff } from 'owa-mail-layout/lib/selectors/readingPanePosition';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import getMailListLoadState from 'owa-mail-list-store/lib/utils/getMailListLoadState';
import { MultiSelectReadingPane } from 'owa-mail-multiselect-reading-pane-view';
import setShouldShowMobileUpsellEmptyState from 'owa-mail-reading-pane-store/lib/actions/setShouldShowMobileUpsellEmptyState';
import ReadingPaneStore from 'owa-mail-reading-pane-store/lib/store/schema/ReadingPaneStore';
import readingPaneStore from 'owa-mail-reading-pane-store/lib/store/Store';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getConversationReadingPaneViewState';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import { MailListViewState } from 'owa-mail-store/lib/store/schema/MailListViewState';
import { hasQueryStringParameter } from 'owa-querystring';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { isSxSDisplayedAndShouldHideConversationReadingPane } from 'owa-sxs-store';
import { getActiveContentTab, TabType } from 'owa-tab-store';
import { getIsLightBaseTheme } from 'owa-theme';
import * as trace from 'owa-trace';
import * as React from 'react';
import { reactive } from 'satcheljs/lib/legacy/react';
import {
    getSelectedTableView,
    MailRowDataPropertyGetter,
    listViewStore,
} from 'owa-mail-list-store';
import {
    GetOutlookMobileComponent,
    OutlookMobileContainer,
    lazyIsOutlookAppInstalled,
} from 'owa-guided-setup-cards';

const styles = require('./ReadingPane.scss');

export interface ReadingPaneProps {
    readingPaneStore?: ReadingPaneStore;
    isInVirtualSelectAllMode?: boolean;
    listViewType?: ReactListViewType;
    isMailListLoaded?: boolean;
    selectedRowKeys?: string[];
    selectedItemPartsCount?: number;
    isDeeplink?: boolean;
    isDumpsterOrDumpsterSearchTable?: boolean;
    isNotesFolder?: boolean;
    sxsId?: string;
}

@reactive({
    readingPaneStore: () => readingPaneStore,
    isInVirtualSelectAllMode: () =>
        getSelectedTableView() && getSelectedTableView().isInVirtualSelectAllMode,
    listViewType: () => getSelectedTableView() && getSelectedTableView().tableQuery.listViewType,
    isMailListLoaded: () => getMailListLoadState() == MailListViewState.Loaded,
    selectedRowKeys: () =>
        getSelectedTableView() && [...getSelectedTableView().selectedRowKeys?.keys()],
    selectedItemPartsCount: () =>
        listViewStore.expandedConversationViewState.selectedNodeIds.length,
})
@observer
export default class ReadingPane extends React.Component<ReadingPaneProps, {}> {
    constructor(props: ReadingPaneProps) {
        super(props);

        if (isGetMobileFeatureEnabled()) {
            lazyIsOutlookAppInstalled.import().then(isAppInstalled => {
                isAppInstalled().then(isInstalled => {
                    setShouldShowMobileUpsellEmptyState(!isInstalled);
                });
            });
        }
        // Intialize the get item manager for reading pane.
        lazyInitializeGetItemManagerForRP.import().then(initializeGetItemManagerForRP => {
            initializeGetItemManagerForRP();
        });
    }

    render() {
        const {
            readingPaneStore,
            isInVirtualSelectAllMode,
            isMailListLoaded,
            selectedRowKeys,
            sxsId,
        } = this.props;
        const {
            itemPrintPaneViewState,
            deeplinkId,
            shouldShowMobileUpsellEmptyState,
        } = readingPaneStore;
        const itemReadingPaneViewState = getItemReadingPaneViewState();
        const conversationReadingPaneViewState = getConversationReadingPaneViewState();
        const activeTab = getActiveContentTab();
        const shouldHideConversationReadingPane = isSxSDisplayedAndShouldHideConversationReadingPane(
            sxsId
        );

        let readingPaneContent: JSX.Element = <NullReadingPane />;

        if (deeplinkId) {
            // Deeplink UIs.
            readingPaneContent = deeplinkId.isPrint ? (
                <PrintItemReadingPane itemId={deeplinkId.id} viewState={itemPrintPaneViewState} />
            ) : (
                <ItemReadingPane
                    itemId={{
                        mailboxInfo: deeplinkId.mailboxInfo,
                        Id: deeplinkId.id,
                    }}
                    itemReadingPaneViewState={itemReadingPaneViewState}
                    isDeeplink={true}
                />
            );
        } else if (activeTab && activeTab.type == TabType.SecondaryReadingPane) {
            const secondaryReadingPaneTabData = activeTab.data;
            switch (secondaryReadingPaneTabData.listViewType) {
                case ReactListViewType.Conversation:
                    if (!shouldHideConversationReadingPane) {
                        if (secondaryReadingPaneTabData.id) {
                            readingPaneContent = (
                                <ConversationReadingPane
                                    conversationId={secondaryReadingPaneTabData.id}
                                    conversationSubject={secondaryReadingPaneTabData.subject}
                                    conversationCategories={secondaryReadingPaneTabData.categories}
                                    sxsId={sxsId}
                                />
                            );
                        } else {
                            trace.errorThatWillCauseAlert(
                                'ReadingPane.render() secondaryReadingPaneTabData.id should not be null.'
                            );
                        }
                    }
                    break;
                case ReactListViewType.Message:
                    readingPaneContent = (
                        <ItemReadingPane
                            itemId={secondaryReadingPaneTabData.id}
                            itemSubject={secondaryReadingPaneTabData.subject}
                            itemReadingPaneViewState={itemReadingPaneViewState}
                        />
                    );
                    break;
            }
        } else if (
            conversationReadingPaneViewState?.conversationId &&
            findInlineComposeViewState(conversationReadingPaneViewState.conversationId.Id)
        ) {
            // If RP has inline compose open, render conversation in RP store.
            if (!shouldHideConversationReadingPane) {
                readingPaneContent = (
                    <ConversationReadingPane
                        conversationId={conversationReadingPaneViewState.conversationId}
                        conversationSubject={conversationReadingPaneViewState.conversationSubject}
                        conversationCategories={
                            conversationReadingPaneViewState.conversationCategories
                        }
                        sxsId={sxsId}
                    />
                );
            }
        } else if (
            selectedRowKeys?.length == 1 &&
            this.props.selectedItemPartsCount <= 1 &&
            !isInVirtualSelectAllMode &&
            !this.props.isDumpsterOrDumpsterSearchTable
        ) {
            // 1 row selected: display reading pane only if select all is not checked and not in dumpster or dumpster search
            const rowId = MailRowDataPropertyGetter.getRowClientItemId(
                selectedRowKeys[0],
                getSelectedTableView()
            );
            const rowSubject = MailRowDataPropertyGetter.getSubject(
                selectedRowKeys[0],
                getSelectedTableView()
            );
            const rowCategories = MailRowDataPropertyGetter.getCategories(
                selectedRowKeys[0],
                getSelectedTableView()
            );
            switch (this.props.listViewType) {
                case ReactListViewType.Conversation:
                    if (!shouldHideConversationReadingPane) {
                        if (rowId) {
                            const instrumentationContext = getInstrumentationContextsFromTableView(
                                [selectedRowKeys[0]],
                                getSelectedTableView()
                            )[0];

                            readingPaneContent = (
                                <ConversationReadingPane
                                    conversationId={rowId}
                                    conversationSubject={rowSubject}
                                    conversationCategories={rowCategories}
                                    instrumentationContext={instrumentationContext}
                                    sxsId={sxsId}
                                />
                            );
                        } else {
                            trace.errorThatWillCauseAlert(
                                'ReadingPane.render() rowId should not be null.'
                            );
                        }
                    }
                    break;
                case ReactListViewType.Message:
                    readingPaneContent = (
                        <ItemReadingPane
                            itemId={rowId}
                            itemSubject={rowSubject}
                            itemReadingPaneViewState={itemReadingPaneViewState}
                        />
                    );
                    break;
            }
        } else if (
            isInVirtualSelectAllMode ||
            selectedRowKeys?.length > 0 ||
            this.props.selectedItemPartsCount > 1
        ) {
            // Multiple items selected or all items are selected
            readingPaneContent = (
                <MultiSelectReadingPane
                    isDumpsterOrDumpsterSearchTable={this.props.isDumpsterOrDumpsterSearchTable}
                    isNotesFolder={this.props.isNotesFolder}
                />
            );
        } else if (isMailListLoaded || readingPaneStore.shouldShowEmptyReadingPane) {
            const DUMPSTER_SVG = getIsLightBaseTheme()
                ? require('../svg/colored/noSelectionDumpsterFolder.svg')
                : require('../svg/gray/noSelectionDumpsterFolder.svg');
            const mobilePromoOverrideParam = 'emptyStateMobilePromo';

            // Empty state.
            readingPaneContent =
                (isGetMobileFeatureEnabled() && shouldShowMobileUpsellEmptyState) ||
                hasQueryStringParameter(mobilePromoOverrideParam) ? (
                    <div className={styles.getOutlookMobileContainer}>
                        <div className={styles.getOutlookMobileComponent}>
                            <GetOutlookMobileComponent
                                containerName={OutlookMobileContainer.ReadingPane}
                                onComponentUnmount={onOutlookMobileUnmount}
                            />
                        </div>
                    </div>
                ) : (
                    <EmptyStateReadingPane
                        emptyStateIcon={
                            this.props.isDumpsterOrDumpsterSearchTable ? DUMPSTER_SVG : null
                        }
                        emptyStateText={
                            this.props.isDumpsterOrDumpsterSearchTable
                                ? loc(recoverableItemsFolderDescription)
                                : null
                        }
                    />
                );
        }

        function onOutlookMobileUnmount() {
            setShouldShowMobileUpsellEmptyState(false);
        }

        return (
            <div key="readingPaneDiv" className={styles.absolutePosition}>
                {readingPaneContent}
                {itemPrintPaneViewState?.itemId && (
                    <PrintPanel
                        itemId={itemPrintPaneViewState.itemId}
                        viewState={itemPrintPaneViewState}
                    />
                )}
            </div>
        );
    }

    componentWillUnmount() {
        if (isReadingPanePositionOff()) {
            updateAddinOnItemNavigation(SelectionType.NotSupported);
        }
    }
}
